import React, { useMemo, useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  IconButton,
  Collapse,
  Alert,
  AlertTitle,
  Grid,
  alpha,
  useTheme,
  CircularProgress,
  Autocomplete,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useGrafanaMetrics } from '../../hooks/useGrafanaMetrics';
import { TimeseriesCard } from '../metrics/TimeseriesCard';
import {
  TELEMETRY_CHART_PANEL_IDS,
  TELEMETRY_PANEL_LABELS,
  DEFAULT_EXECUTION_TELEMETRY_PANELS,
  EXECUTION_TELEMETRY_PANELS_STORAGE_KEY,
  EXECUTION_VISIBLE_METRICS_PICKER_EXPANDED_KEY,
  parseStoredTelemetryPanelIds,
  sortTelemetryPanelIds,
  buildProviderSeriesFromBotsByProvider,
  computeBotsByProviderYDomain,
  getTelemetryTimeseriesPanelConfig,
  type TelemetryChartPanelId,
} from '../../utils/telemetryTimeseriesPanels';

const LIVE_DEFAULT_PANELS: TelemetryChartPanelId[] = ['bandwidth', 'packets', 'tcp', 'rps'];

interface MetricsPanelProps {
  isCollapsed: boolean;
  onToggle: () => void;
  tenantId?: string | null;
  scenarioId?: string | null;
  scenarioName?: string | null;
  /** Attack class of the scenario in view; relabels the RPS card for socket attacks. */
  attackClass?: string | null;
  /** When set, load this window once (no live polling). Used for past execution time ranges. */
  fixedRangeSec?: { startSec: number; endSec: number } | null;
  /** `embedded`: no collapse toggle; content always visible (e.g. executions detail). */
  variant?: 'card' | 'embedded';
  /** Override default card title. */
  title?: string;
  /** Override default footer caption. */
  footer?: string;
  /**
   * When true, show a multi-select of telemetry charts (same catalog as Dashboard) and persist selection.
   * Used on the Executions page; Operations live view keeps a fixed set.
   */
  configurablePanels?: boolean;
  /** Sync vertical cursor + header values (e.g. execution log row hover), ms since epoch. */
  syncReferenceTimeMs?: number | null;
  /**
   * When provided, clicking a point on any chart reports its timestamp so the caller can
   * lock the shared cursor across every chart. Called with null when the click missed
   * a data point.
   */
  onSelectReferenceTime?: (timeMs: number | null) => void;
  /** True when `syncReferenceTimeMs` is pinned rather than following the pointer. */
  referenceLocked?: boolean;
  /** Executions page: click timeline icon in header to hide the metrics column. */
  onEmbeddedAvatarClick?: () => void;
}

const MetricsPanel: React.FC<MetricsPanelProps> = ({
  isCollapsed,
  onToggle,
  tenantId,
  scenarioId,
  scenarioName,
  attackClass,
  fixedRangeSec,
  variant = 'card',
  title: titleProp,
  footer: footerProp,
  configurablePanels = false,
  syncReferenceTimeMs = null,
  onSelectReferenceTime,
  referenceLocked = false,
  onEmbeddedAvatarClick,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isEmbedded = variant === 'embedded';
  const rangeSeconds =
    fixedRangeSec &&
    Number.isFinite(fixedRangeSec.startSec) &&
    Number.isFinite(fixedRangeSec.endSec)
      ? Math.max(60, Math.floor(fixedRangeSec.endSec) - Math.floor(fixedRangeSec.startSec))
      : 300;

  const [executionPanelIds, setExecutionPanelIds] = useState<TelemetryChartPanelId[]>(
    DEFAULT_EXECUTION_TELEMETRY_PANELS
  );

  const [visibleMetricsPickerExpanded, setVisibleMetricsPickerExpanded] = useState(() => {
    try {
      const raw = localStorage.getItem(EXECUTION_VISIBLE_METRICS_PICKER_EXPANDED_KEY);
      if (raw === null) return false;
      return JSON.parse(raw) === true;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!configurablePanels) return;
    setExecutionPanelIds(
      parseStoredTelemetryPanelIds(localStorage.getItem(EXECUTION_TELEMETRY_PANELS_STORAGE_KEY))
    );
  }, [configurablePanels]);

  useEffect(() => {
    if (!configurablePanels) return;
    try {
      localStorage.setItem(EXECUTION_TELEMETRY_PANELS_STORAGE_KEY, JSON.stringify(executionPanelIds));
    } catch {
      /* ignore */
    }
  }, [configurablePanels, executionPanelIds]);

  useEffect(() => {
    if (!configurablePanels) return;
    try {
      localStorage.setItem(
        EXECUTION_VISIBLE_METRICS_PICKER_EXPANDED_KEY,
        JSON.stringify(visibleMetricsPickerExpanded)
      );
    } catch {
      /* ignore */
    }
  }, [configurablePanels, visibleMetricsPickerExpanded]);

  const { metrics, error, loading } = useGrafanaMetrics({
    tenantId,
    scenarioId,
    refreshInterval: fixedRangeSec ? 60_000 : 5000,
    timeRange: rangeSeconds,
    range: fixedRangeSec ?? null,
  });

  const xDomain = useMemo<[number, number]>(() => {
    if (
      fixedRangeSec &&
      Number.isFinite(fixedRangeSec.startSec) &&
      Number.isFinite(fixedRangeSec.endSec)
    ) {
      const startMs = Math.floor(fixedRangeSec.startSec) * 1000;
      const endMs = Math.floor(fixedRangeSec.endSec) * 1000;
      if (endMs > startMs) return [startMs, endMs];
    }
    const timestamps = [
      ...metrics.bandwidthDown,
      ...metrics.bandwidthUp,
      ...metrics.packetsUp,
      ...metrics.packetsDown,
      ...metrics.tcpConnections,
      ...metrics.tcpConnectionsClosed,
      ...metrics.rpsTotal,
    ].map((p) => p.timestamp);
    const end = timestamps.length > 0 ? Math.max(...timestamps) : Date.now();
    return [end - 5 * 60 * 1000, end];
  }, [
    fixedRangeSec,
    metrics.bandwidthDown,
    metrics.bandwidthUp,
    metrics.packetsUp,
    metrics.packetsDown,
    metrics.tcpConnections,
    metrics.tcpConnectionsClosed,
    metrics.rpsTotal,
  ]);

  const providerSeries = useMemo(
    () => buildProviderSeriesFromBotsByProvider(metrics.botsByProvider, theme),
    [metrics.botsByProvider, theme]
  );

  const botsByProviderYDomain = useMemo(
    () => computeBotsByProviderYDomain(providerSeries, xDomain),
    [providerSeries, xDomain]
  );

  const botsByProviderYAxisPadding = useMemo(
    () => (botsByProviderYDomain ? ({ bottom: 10 } as const) : undefined),
    [botsByProviderYDomain]
  );

  const panelIdsToRender = configurablePanels ? executionPanelIds : LIVE_DEFAULT_PANELS;
  const chartHeight = configurablePanels ? 190 : 170;

  const cardTitle =
    titleProp ?? (fixedRangeSec ? 'Execution metrics' : 'Live Metrics');
  const footerCaption =
    footerProp ??
    (fixedRangeSec
      ? 'Telemetry for the selected execution window (Prometheus range query)'
      : '⚡ Live scenario telemetry • Updates every 5 seconds • Last 5 minutes');

  const showCollapse = !isEmbedded;
  const collapsed = isEmbedded ? false : isCollapsed;

  const headerAvatar = isEmbedded && onEmbeddedAvatarClick ? (
    <Tooltip title="Hide metrics panel">
      <span>
        <IconButton
          onClick={onEmbeddedAvatarClick}
          aria-label="Hide metrics panel"
          sx={{
            p: 1,
            borderRadius: '12px',
            backgroundColor: alpha(theme.palette.secondary.main, isDark ? 0.2 : 0.25),
            border: `2px solid ${alpha(theme.palette.secondary.main, isDark ? 0.4 : 0.35)}`,
            color: theme.palette.secondary.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.secondary.main, isDark ? 0.32 : 0.38),
            },
          }}
        >
          <TimelineIcon sx={{ fontSize: '1.5rem' }} />
        </IconButton>
      </span>
    </Tooltip>
  ) : (
    <Box
      sx={{
        backgroundColor: alpha(theme.palette.secondary.main, isDark ? 0.2 : 0.25),
        borderRadius: '12px',
        p: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `2px solid ${alpha(theme.palette.secondary.main, isDark ? 0.4 : 0.35)}`,
      }}
    >
      <TimelineIcon sx={{ color: theme.palette.secondary.main }} />
    </Box>
  );

  return (
    <Card
      sx={{
        backgroundColor: 'background.paper',
        backdropFilter: 'blur(12px)',
        border: `1px solid ${alpha(theme.palette.secondary.main, isDark ? 0.2 : 0.25)}`,
        transition: 'all 0.3s ease',
      }}
    >
      <CardHeader
        avatar={headerAvatar}
        action={
          showCollapse ? (
            <IconButton onClick={onToggle}>
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          ) : undefined
        }
        title={
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {cardTitle}
          </Typography>
        }
        subheader={
          collapsed && scenarioName && (
            <Typography
              variant="caption"
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: 'text.secondary',
              }}
            >
              {scenarioName}
            </Typography>
          )
        }
        sx={{
          '& .MuiCardHeader-content': {
            overflow: 'hidden',
          },
        }}
      />

      <Collapse in={!collapsed} timeout="auto">
        <CardContent>
          {!tenantId || !scenarioId ? (
            <Alert
              severity="info"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.1 : 0.12),
                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.25)}`,
                '& .MuiAlert-icon': {
                  color: theme.palette.primary.main,
                },
              }}
            >
              <AlertTitle sx={{ fontFamily: '"Space Mono", monospace', color: theme.palette.primary.main }}>
                No Active Scenario
              </AlertTitle>
              <Typography sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.875rem' }}>
                Select a scenario to view live metrics
              </Typography>
            </Alert>
          ) : error ? (
            <Alert
              severity="error"
              sx={{
                backgroundColor: alpha(theme.palette.error.main, isDark ? 0.1 : 0.12),
                border: `1px solid ${alpha(theme.palette.error.main, isDark ? 0.3 : 0.25)}`,
              }}
            >
              {error}
            </Alert>
          ) : loading &&
            metrics.bandwidthDown.length === 0 &&
            metrics.bandwidthUp.length === 0 &&
            metrics.rpsTotal.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box>
              {configurablePanels && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-expanded={visibleMetricsPickerExpanded}
                    aria-controls="execution-visible-metrics-picker"
                    id="execution-visible-metrics-picker-label"
                    onClick={() => setVisibleMetricsPickerExpanded((v) => !v)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setVisibleMetricsPickerExpanded((v) => !v);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                      cursor: 'pointer',
                      userSelect: 'none',
                      py: 0.75,
                      px: 0.5,
                      borderRadius: 1,
                      mb: visibleMetricsPickerExpanded ? 1 : 0.5,
                      '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.08) },
                      '&:focus-visible': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        component="span"
                        sx={{ fontFamily: '"Space Mono", monospace', fontWeight: 600, display: 'block' }}
                      >
                        Visible metrics
                      </Typography>
                      {!visibleMetricsPickerExpanded && (
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{
                            display: 'block',
                            mt: 0.35,
                            fontFamily: '"Space Mono", monospace',
                            fontSize: '0.68rem',
                            lineHeight: 1.35,
                            color: 'text.secondary',
                          }}
                        >
                          Click to choose which charts are shown — same catalog as the Dashboard.
                        </Typography>
                      )}
                    </Box>
                    {visibleMetricsPickerExpanded ? (
                      <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0, mt: 0.25 }} />
                    ) : (
                      <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0, mt: 0.25 }} />
                    )}
                  </Box>
                  <Collapse in={visibleMetricsPickerExpanded} timeout="auto">
                    <Box id="execution-visible-metrics-picker">
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={TELEMETRY_CHART_PANEL_IDS}
                        value={executionPanelIds}
                        onChange={(_, newValue) => {
                          const sorted = sortTelemetryPanelIds((newValue as TelemetryChartPanelId[]) || []);
                          setExecutionPanelIds(
                            sorted.length > 0 ? sorted : [...DEFAULT_EXECUTION_TELEMETRY_PANELS]
                          );
                        }}
                        getOptionLabel={(id) => TELEMETRY_PANEL_LABELS[id]}
                        isOptionEqualToValue={(a, b) => a === b}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Charts to show"
                            placeholder="Same catalog as Dashboard"
                            size="small"
                            aria-labelledby="execution-visible-metrics-picker-label"
                            sx={{
                              '& .MuiInputLabel-root': {
                                fontFamily: '"Space Mono", monospace',
                                fontSize: '0.8rem',
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Collapse>
                </Box>
              )}

              <Grid container spacing={2}>
                {panelIdsToRender.map((panelId) => {
                  const config = getTelemetryTimeseriesPanelConfig(panelId, {
                    metrics,
                    theme,
                    providerSeries,
                    botsByProviderYDomain: panelId === 'botsByProvider' ? botsByProviderYDomain : undefined,
                    botsByProviderYAxisPadding:
                      panelId === 'botsByProvider' ? botsByProviderYAxisPadding : undefined,
                    chartHeight,
                    attackClass,
                  });
                  if (!config) return null;
                  return (
                    <Grid item xs={12} key={panelId}>
                      <TimeseriesCard
                        title={config.title}
                        icon={config.icon}
                        height={config.height}
                        unit={config.unit}
                        xDomain={xDomain}
                        series={config.series}
                        yTickFormatter={config.yTickFormatter}
                        valueFormatter={config.valueFormatter}
                        yAxisAllowDecimals={config.yAxisAllowDecimals}
                        yDomain={config.yDomain}
                        yAxisPadding={config.yAxisPadding}
                        referenceTimeMs={syncReferenceTimeMs}
                        onSelectReferenceTime={onSelectReferenceTime}
                        referenceLocked={referenceLocked}
                      />
                    </Grid>
                  );
                })}
              </Grid>

              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontFamily: '"Space Mono", monospace',
                  display: 'block',
                  mt: 3,
                  textAlign: 'center',
                  fontSize: '0.7rem',
                }}
              >
                {footerCaption}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default React.memo(MetricsPanel);
